import { Alert, Badge, Group, Paper, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { formatDateTime, formatMoney } from '../../lib/format';

export const QUALITY_RECOVERY_PATH = '/admin/v1/reports/quality-recovery';

interface RecoveryRow {
  recoveryId: string;
  at: string;
  businessDate: string;
  daypart: 'morning' | 'midday' | 'evening' | 'lateNight';
  action: 'recall' | 'remake' | 'credit' | 'remakeAndCredit';
  station: 'kitchen' | 'bar';
  reasonNameEn: string;
  staffName: string;
  itemNameEn: string;
  remadeOrderLineId: string | null;
  appliedDiscountId: string | null;
  wasMade: boolean;
  wastedCostBaseMinor: number;
  remadeCostBaseMinor: number;
  creditedRevenueBaseMinor: number;
  failureCostBaseMinor: number;
}

interface QualityRecoveryReport {
  baseCurrency: string;
  rows: RecoveryRow[];
  totals: {
    recoveries: number;
    remakes: number;
    credits: number;
    wastedCostBaseMinor: number;
    remadeCostBaseMinor: number;
    creditedRevenueBaseMinor: number;
    failureCostBaseMinor: number;
  };
}

const actionName: Record<RecoveryRow['action'], string> = {
  recall: 'Send cancelled',
  remake: 'Remade',
  credit: 'Guest credited',
  remakeAndCredit: 'Remade + credited',
};

const daypartName: Record<RecoveryRow['daypart'], string> = {
  morning: 'Morning',
  midday: 'Midday',
  evening: 'Evening',
  lateNight: 'Late night',
};

function Figure({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text fw={600}>{value}</Text>
    </div>
  );
}

/** The exact history behind “did we remake it, and did we take it off the bill?” */
export function QualityRecovery({ from, to }: { from: string; to: string }) {
  const report = useQuery({
    queryKey: ['quality-recovery', from, to],
    queryFn: () =>
      rawApi<QualityRecoveryReport>(
        'GET',
        `${QUALITY_RECOVERY_PATH}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });
  const data = report.data;
  const base = data?.baseCurrency ?? 'USD';

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>Food that came back</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm" maw={880}>
        Every problem item, what staff chose for that exact plate, and what the failure cost. A replacement
        and a credit remain separate here because “we made it again” and “the guest did not pay” are two
        different promises.
      </Text>

      {report.isError && <Alert color="red">That recovery history could not be loaded.</Alert>}

      {data && data.totals.recoveries === 0 && (
        <Alert color="gray" variant="light">
          No food was recalled, remade or credited in these dates.
        </Alert>
      )}

      {data && data.totals.recoveries > 0 && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
            <Figure label="Problem items" value={data.totals.recoveries} />
            <Figure label="Replacements made" value={data.totals.remakes} />
            <Figure label="Guest credits" value={data.totals.credits} />
            <Figure label="Total failure cost" value={formatMoney(data.totals.failureCostBaseMinor, base)} />
          </SimpleGrid>
          <Text size="xs" c="dimmed" mb="sm">
            Failure cost is {formatMoney(data.totals.wastedCostBaseMinor, base)} of failed food +{' '}
            {formatMoney(data.totals.remadeCostBaseMinor, base)} of replacements +{' '}
            {formatMoney(data.totals.creditedRevenueBaseMinor, base)} credited to guests.
          </Text>

          <Table.ScrollContainer minWidth={980}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Item and outcome</Table.Th>
                  <Table.Th>When</Table.Th>
                  <Table.Th>Station</Table.Th>
                  <Table.Th>Reason</Table.Th>
                  <Table.Th>Staff</Table.Th>
                  <Table.Th ta="right">Failure cost</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.rows.map((row) => (
                  <Table.Tr key={row.recoveryId}>
                    <Table.Td>
                      <Text fw={500}>{row.itemNameEn}</Text>
                      <Group gap={6} mt={4}>
                        <Badge size="sm" variant="light" color={row.appliedDiscountId ? 'orange' : 'blue'}>
                          {actionName[row.action]}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          Original {row.wasMade ? 'was made' : 'was not made'}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">{formatDateTime(row.at)}</Text>
                        <Text size="xs" c="dimmed">
                          {daypartName[row.daypart]}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td tt="capitalize">{row.station}</Table.Td>
                    <Table.Td>{row.reasonNameEn}</Table.Td>
                    <Table.Td>{row.staffName}</Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatMoney(row.failureCostBaseMinor, base)}</Text>
                      {row.remadeOrderLineId && (
                        <Text size="xs" c="dimmed">
                          replacement {formatMoney(row.remadeCostBaseMinor, base)}
                        </Text>
                      )}
                      {row.appliedDiscountId && (
                        <Text size="xs" c="dimmed">
                          guest credit {formatMoney(row.creditedRevenueBaseMinor, base)}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
    </Paper>
  );
}
